import {
  DISPLAY_NAME_MAX_LENGTH,
  DISPLAY_NAME_MIN_LENGTH,
  SessionListResponseSchema,
  type Entitlements,
  type Session,
} from "@legacy-manager/shared";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { dataLonga, tempoRelativo, useT } from "../i18n";
import { useErroEmToast } from "../ui/avisos";
import {
  CancelamentoSchema,
  ExclusaoSchema,
  PerfilSchema,
} from "../online/esquemas";
import { mensagemDoErro, useSessao, type DadosDaConta } from "../online/sessao";
import { Panel, Stat } from "../ui/atoms";
import { Campo } from "./Campo";
import { Pagina } from "../ui/Pagina";

/**
 * Conta: o painel de quem já entrou.
 *
 * **Entrar não acontece mais aqui.** O formulário mora na abertura, embaixo do
 * emblema, e esta tela deixou de ter as duas caras que tinha: quem chega sem
 * sessão é mandado de volta para lá em vez de ver um segundo formulário igual.
 * Dois lugares para a mesma coisa era um deles fatalmente ficando para trás.
 *
 * O que sobrou é de quem já entrou: **jogar sozinho não exige conta nenhuma**
 * (docs/LEGAL.md 0), e quem chega aqui quer comprar algo ou mexer nos próprios
 * dados.
 *
 * A parte mais delicada é a exclusão, e ela segue docs/LEGAL.md 3 à risca: é
 * autoatendimento, tem 7 dias de arrependimento, e a tela diz na cara o que
 * some e o que fica.
 *
 * **A exportação de dados saiu daqui.** O que é do jogador de verdade é o
 * progresso do modo 1 Jogador, que fica no aparelho e se salva na Carreira; a
 * liga é outro jogo, com outro time. A cópia dos dados da conta é pedida por
 * e-mail, como diz docs/LEGAL.md 1.7.
 *
 * **Quais avisos a pessoa quer não mora mais aqui**, e sim em
 * `src/screens/Opcoes.tsx`. É preferência, não identidade: fica junto de idioma
 * e de save na nuvem, e ter as caixinhas em dois lugares seria o mesmo estado
 * desenhado duas vezes, com a chance de as duas telas discordarem. O que sobra
 * nesta é o que só existe por causa da conta: quem é você, onde você entrou, o
 * que você comprou e o que é seu para levar ou apagar.
 */

export function Conta({
  aoVoltar,
}: {
  aoVoltar?: () => void;
}): ReactNode {
  const t = useT();
  const sessao = useSessao();

  /*
   * Sem sessão, esta tela não tem nada a oferecer: sai dela.
   *
   * Acontece em dois momentos reais. Quem sai da conta estando aqui, e quem
   * volta de um atalho antigo para uma tela que hoje é só de quem entrou. Nos
   * dois casos o destino é a abertura, que é onde o formulário está.
   *
   * O `aoVoltar` é opcional porque o hub também desenha esta tela, lá dentro da
   * carreira, e lá ela só aparece com sessão em pé: não há para onde voltar
   * nem de que voltar.
   */
  const visitante = sessao.estado === "visitante";
  useEffect(() => {
    if (visitante) aoVoltar?.();
  }, [visitante, aoVoltar]);

  return (
    <Pagina titulo={t("conta.titulo")} aoVoltar={aoVoltar}>
      {sessao.estado === "conectada" && sessao.conta ? (
        <PainelDaConta conta={sessao.conta} />
      ) : (
        <p className="muted" style={{ textAlign: "center" }}>
          {t("online.carregando")}
        </p>
      )}
    </Pagina>
  );
}

// ---------------------------------------------------------------------------
// Painel de quem já está dentro
// ---------------------------------------------------------------------------

function PainelDaConta({
  conta,
}: {
  conta: DadosDaConta;
}): ReactNode {
  const t = useT();
  const sessao = useSessao();
  const [erro, definirErro] = useState<string | null>(null);
  useErroEmToast(erro);
  const [aviso, definirAviso] = useState<string | null>(null);
  const [ocupado, definirOcupado] = useState(false);

  /** Envolve toda ação de rede desta tela: um erro visível, um estado de espera. */
  const executar = useCallback(async (acao: () => Promise<string | null>): Promise<void> => {
    definirErro(null);
    definirAviso(null);
    definirOcupado(true);
    try {
      definirAviso(await acao());
    } catch (causa) {
      definirErro(mensagemDoErro(causa));
    } finally {
      definirOcupado(false);
    }
  }, []);

  return (
    <div style={{ display: "grid", gap: 14 }}>
      {aviso && (
        <p className="label" style={{ margin: 0, lineHeight: 1.6 }}>
          {aviso}
        </p>
      )}

      {!conta.usuario.emailVerified && (
        <Panel>
          <p style={{ margin: 0, fontSize: 12.5, color: "var(--warning)" }}>
            {t("conta.emailNaoConfirmado")}
          </p>
          <button
            className="ghost"
            disabled={ocupado}
            style={{ marginTop: 10 }}
            onClick={() =>
              void executar(async () => {
                await sessao.cliente.pedirVazio({
                  metodo: "POST",
                  caminho: "/v1/auth/resend-verification",
                });
                return t("conta.confirmacaoReenviada");
              })
            }
          >
            {t("conta.reenviarConfirmacao")}
          </button>
        </Panel>
      )}

      <Perfil conta={conta} ocupado={ocupado} executar={executar} />
      <Dispositivos ocupado={ocupado} executar={executar} />
      <Direitos direitos={conta.direitos} />
      <Dados ocupado={ocupado} executar={executar} />

      <button className="ghost" disabled={ocupado} onClick={() => void sessao.sair()}>
        {t("conta.sairDaConta")}
      </button>
    </div>
  );
}

type Executar = (acao: () => Promise<string | null>) => Promise<void>;

function Perfil({
  conta,
  ocupado,
  executar,
}: {
  conta: DadosDaConta;
  ocupado: boolean;
  executar: Executar;
}): ReactNode {
  const t = useT();
  const sessao = useSessao();
  const [nome, definirNome] = useState(conta.usuario.displayName);

  return (
    <Panel title={t("conta.perfil")}>
      <div style={{ display: "grid", gap: 12 }}>
        <Stat label={t("conta.email")}>{conta.usuario.email}</Stat>
        <Campo
          rotulo={t("conta.nome")}
          ajuda={t("conta.nomeAjuda", {
            minimo: DISPLAY_NAME_MIN_LENGTH,
            maximo: DISPLAY_NAME_MAX_LENGTH,
          })}
        >
          <input
            value={nome}
            onChange={(evento) => definirNome(evento.target.value)}
            minLength={DISPLAY_NAME_MIN_LENGTH}
            maxLength={DISPLAY_NAME_MAX_LENGTH}
          />
        </Campo>
        <p className="label-muted" style={{ margin: 0 }}>
          {t("conta.contaDesde", { data: dataLonga(conta.usuario.createdAt) })}
        </p>
        <button
          className="ghost"
          disabled={ocupado || nome.trim() === conta.usuario.displayName}
          style={{ justifySelf: "start" }}
          onClick={() =>
            void executar(async () => {
              const perfil = await sessao.cliente.pedir(
                { metodo: "PATCH", caminho: "/v1/me", corpo: { displayName: nome.trim() } },
                PerfilSchema,
              );
              sessao.aplicarPerfil(perfil);
              return t("conta.perfilSalvo");
            })
          }
        >
          {ocupado ? t("conta.salvando") : t("conta.salvar")}
        </button>
      </div>
    </Panel>
  );
}

function Dispositivos({ ocupado, executar }: { ocupado: boolean; executar: Executar }): ReactNode {
  const t = useT();
  const sessao = useSessao();
  const [lista, definirLista] = useState<Session[] | null>(null);

  const carregar = useCallback(async () => {
    const resposta = await sessao.cliente.pedir(
      { metodo: "GET", caminho: "/v1/auth/sessions" },
      SessionListResponseSchema,
    );
    definirLista(resposta.items);
  }, [sessao]);

  useEffect(() => {
    void carregar().catch(() => definirLista([]));
  }, [carregar]);

  return (
    <Panel title={t("conta.dispositivos")}>
      <p className="muted" style={{ margin: "0 0 10px", fontSize: 11.5 }}>
        {t("conta.dispositivosAjuda")}
      </p>
      {lista === null && <p className="muted" style={{ margin: 0 }}>{t("online.carregando")}</p>}
      {lista !== null && lista.length === 0 && (
        <p className="muted" style={{ margin: 0 }}>
          {t("conta.semDispositivos")}
        </p>
      )}
      <div className="grid" style={{ gap: 8 }}>
        {(lista ?? []).map((dispositivo) => (
          <div key={dispositivo.id} className="row" style={{ gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600 }}>
                {dispositivo.deviceName}
                {dispositivo.current && (
                  <span className="label" style={{ marginLeft: 8 }}>
                    {t("conta.esteAparelho")}
                  </span>
                )}
              </div>
              <div className="muted" style={{ fontSize: 11.5 }}>
                {dispositivo.ip ? `${dispositivo.ip} · ` : ""}
                {dispositivo.lastUsedAt
                  ? t("conta.ultimoUso", { quando: tempoRelativo(Date.parse(dispositivo.lastUsedAt)) })
                  : t("conta.entrouEm", { quando: tempoRelativo(Date.parse(dispositivo.createdAt)) })}
              </div>
            </div>
            <button
              className="ghost"
              disabled={ocupado}
              style={{ color: "var(--danger)", minHeight: 30, padding: "2px 10px", fontSize: 11.5 }}
              onClick={() =>
                void executar(async () => {
                  await sessao.cliente.pedirVazio({
                    metodo: "DELETE",
                    caminho: `/v1/auth/sessions/${dispositivo.id}`,
                  });
                  // Derrubar a própria sessão encerra o jogo online aqui mesmo,
                  // então não há lista para recarregar depois.
                  if (!dispositivo.current) await carregar();
                  return null;
                })
              }
            >
              {t("conta.derrubar")}
            </button>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function Direitos({ direitos }: { direitos: Entitlements }): ReactNode {
  const t = useT();
  const passe = direitos.pass;

  return (
    <Panel title={t("conta.direitos")}>
      <div
        className="grid"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}
      >
        <Stat label={t("conta.vitalicia")}>
          {direitos.lifetime ? t("conta.temDireito") : t("conta.semDireito")}
        </Stat>
        <Stat label={t("conta.passe")}>
          {passe?.active ? t("conta.temDireito") : t("conta.semDireito")}
        </Stat>
        <Stat label={t("conta.cosmeticos")}>
          {t("conta.cosmeticosTotal", { total: direitos.cosmetics.length })}
        </Stat>
      </div>
      {passe && (
        <p className="muted" style={{ margin: "10px 0 0", fontSize: 11.5 }}>
          {passe.renewsAt
            ? t("conta.renovaEm", { data: dataLonga(passe.renewsAt) })
            : t("conta.semRenovacao")}
          {" · "}
          {t("conta.porOrigem", { origem: t(`conta.origens.${passe.source}`) })}
        </p>
      )}
    </Panel>
  );
}

function Dados({
  ocupado,
  executar,
}: {
  ocupado: boolean;
  executar: Executar;
}): ReactNode {
  const t = useT();
  const sessao = useSessao();
  const [confirmacao, definirConfirmacao] = useState("");
  const [querApagar, definirQuerApagar] = useState(false);
  const [marcada, definirMarcada] = useState<string | null>(null);

  const palavra = t("conta.excluirPalavra");

  return (
    <Panel title={t("conta.dados")}>
      <div style={{ display: "grid", gap: 16 }}>
        <section>
          <h3 style={{ fontSize: 14, color: "var(--danger)" }}>{t("conta.excluirTitulo")}</h3>
          <p className="muted" style={{ margin: "6px 0 8px", fontSize: 12 }}>
            {t("conta.excluirComoFunciona")}
          </p>
          <p className="muted" style={{ margin: "0 0 8px", fontSize: 12 }}>
            {t("conta.excluirOQueSai")}
          </p>
          <div className="label-muted">{t("conta.excluirOQueFica")}</div>
          <ul className="muted" style={{ margin: "6px 0 10px", paddingLeft: 18, fontSize: 12 }}>
            <li>{t("conta.excluirFicaFiscal")}</li>
            <li>{t("conta.excluirFicaLigas")}</li>
            <li>{t("conta.excluirFicaDatapacks")}</li>
            <li>{t("conta.excluirFicaSancao")}</li>
          </ul>

          {marcada ? (
            <div style={{ display: "grid", gap: 10 }}>
              <p style={{ margin: 0, fontSize: 12.5, color: "var(--warning)" }}>
                {t("conta.exclusaoAgendada", { data: dataLonga(marcada) })}
              </p>
              <button
                className="primary"
                disabled={ocupado}
                style={{ justifySelf: "start" }}
                onClick={() =>
                  void executar(async () => {
                    await sessao.cliente.pedir(
                      { metodo: "POST", caminho: "/v1/me/cancel-deletion" },
                      CancelamentoSchema,
                    );
                    definirMarcada(null);
                    definirQuerApagar(false);
                    definirConfirmacao("");
                    return t("conta.exclusaoCancelada");
                  })
                }
              >
                {t("conta.cancelarExclusao")}
              </button>
            </div>
          ) : querApagar ? (
            <div style={{ display: "grid", gap: 10 }}>
              <Campo rotulo={t("conta.excluirConfirmar", { palavra })}>
                <input
                  value={confirmacao}
                  onChange={(evento) => definirConfirmacao(evento.target.value)}
                  autoComplete="off"
                />
              </Campo>
              <div className="row" style={{ gap: 8 }}>
                <button
                  className="ghost"
                  disabled={ocupado}
                  onClick={() => {
                    definirQuerApagar(false);
                    definirConfirmacao("");
                  }}
                >
                  {t("comum.cancelar")}
                </button>
                <button
                  className="ghost"
                  disabled={ocupado || confirmacao.trim().toUpperCase() !== palavra}
                  style={{ color: "var(--danger)" }}
                  onClick={() =>
                    void executar(async () => {
                      const pedido = await sessao.cliente.pedir(
                        { metodo: "DELETE", caminho: "/v1/me" },
                        ExclusaoSchema,
                      );
                      definirMarcada(pedido.scheduledFor);
                      return null;
                    })
                  }
                >
                  {t("conta.excluirAgora")}
                </button>
              </div>
            </div>
          ) : (
            <button
              className="ghost"
              disabled={ocupado}
              style={{ color: "var(--danger)" }}
              onClick={() => definirQuerApagar(true)}
            >
              {t("conta.excluir")}
            </button>
          )}
        </section>
      </div>
    </Panel>
  );
}

// ---------------------------------------------------------------------------
// Peça de formulário
// ---------------------------------------------------------------------------
